import { useNotification } from '@refinedev/core'
import type { PlatformRole, PlatformUser } from '@sirius/contracts/admin'
import { MAX_BAN_REASON_LENGTH } from '@sirius/contracts/admin'
import { useMutation } from '@tanstack/react-query'
import type { FormEvent } from 'react'
import { useId, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAdminClient } from '~/api/adminClientContext'
import { ConfirmDialog } from '~/components/confirmDialog'
import { Button } from '~/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '~/components/ui/dialog'
import { Label } from '~/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~/components/ui/select'
import { Textarea } from '~/components/ui/textarea'
import { errorMessage } from '~/providers/errors'
import { platformRoleActionFor, platformRoleChange } from './userActions'
import {
  platformRoleChoices,
  roleLabel,
  roleValue,
  toPlatformRole,
} from './userFormat'
import { useUserRefresh } from './useUserRefresh'

/**
 * The account's actions on its page: ban (with a reason) or unban, and the
 * platform role (none, technician, admin), each behind a confirmation. A
 * platform admin never bans themselves or changes their own platform role:
 * those show disabled with why.
 */
export function AccountActions({
  user,
  viewerId,
}: {
  user: PlatformUser
  /** the signed-in platform admin */
  viewerId: string | undefined
}) {
  const { t } = useTranslation()
  const client = useAdminClient()
  const refresh = useUserRefresh()
  const { open } = useNotification()
  const isSelf = user.id === viewerId
  const roleId = useId()
  const roleAction = platformRoleActionFor(user, viewerId, t)
  // the role picked in the select, until it is confirmed or cancelled
  const [pendingRole, setPendingRole] = useState<{
    role: PlatformRole | null
  } | null>(null)

  const onError = (message: string) => (error: Error) => {
    open?.({ type: 'error', message, description: errorMessage(error) })
  }
  const unban = useMutation({
    mutationFn: () => client.users.unban({ id: user.id }),
    onSuccess: async () => {
      await refresh(user.id)
      open?.({
        type: 'success',
        message: t('usersPage.account.unbanned', { name: user.name }),
      })
    },
    onError: onError(t('usersPage.account.unbanFailed')),
  })
  const setPlatformRole = useMutation({
    mutationFn: (platformRole: PlatformRole | null) =>
      client.users.setPlatformRole({ id: user.id, platformRole }),
    onSuccess: async (_user, platformRole) => {
      await refresh(user.id)
      open?.({
        type: 'success',
        message:
          platformRole === null
            ? t('usersPage.account.hasNoRole', { name: user.name })
            : t('usersPage.account.hasRole', {
                name: user.name,
                role: roleLabel(platformRole, t),
              }),
      })
    },
    onError: onError(t('usersPage.account.roleFailed')),
  })
  const isPending = unban.isPending || setPlatformRole.isPending
  const change =
    pendingRole === null
      ? null
      : platformRoleChange(user.platformRole, pendingRole.role, t, user.name)

  return (
    <section
      aria-labelledby="account-heading"
      className="space-y-2"
    >
      <h3
        id="account-heading"
        className="font-semibold"
      >
        {t('usersPage.account.heading')}
      </h3>
      <div className="flex flex-wrap gap-2">
        {user.banned ? (
          <ConfirmDialog
            trigger={
              <Button
                variant="outline"
                size="sm"
                disabled={isPending}
              >
                {t('usersPage.account.unban')}
              </Button>
            }
            title={t('usersPage.account.unbanTitle', { name: user.name })}
            description={t('usersPage.account.unbanDescription')}
            confirmLabel={t('usersPage.account.unban')}
            onConfirm={() => unban.mutate()}
          />
        ) : (
          <BanDialog
            user={user}
            disabled={isPending || isSelf}
          />
        )}
        <div className="flex items-center gap-2">
          <Label htmlFor={roleId}>{t('usersPage.account.platformRole')}</Label>
          <Select
            value={roleValue(user.platformRole)}
            disabled={isPending || roleAction.disabledReason !== null}
            onValueChange={(value) => {
              const role = toPlatformRole(value)
              if (role !== user.platformRole) {
                setPendingRole({ role })
              }
            }}
          >
            <SelectTrigger
              id={roleId}
              size="sm"
              className="w-40"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {platformRoleChoices(t).map(({ value, label }) => (
                <SelectItem
                  key={value}
                  value={value}
                >
                  {label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {change !== null && pendingRole !== null && (
          <ConfirmDialog
            open
            onOpenChange={(next) => {
              if (!next) {
                setPendingRole(null)
              }
            }}
            title={change.confirmTitle}
            description={change.description}
            confirmLabel={change.confirmLabel}
            destructive={change.destructive}
            onConfirm={() => setPlatformRole.mutate(pendingRole.role)}
          />
        )}
      </div>
      {isSelf && (
        <p className="text-muted-foreground text-sm">
          {t('usersPage.account.selfNote')}
        </p>
      )}
    </section>
  )
}

/**
 * "Ban": a reason (required, shown to staff while the ban lasts), then the
 * ban, which also signs the user out everywhere. A failed ban keeps the
 * dialog open with why.
 */
function BanDialog({
  user,
  disabled,
}: {
  user: PlatformUser
  disabled: boolean
}) {
  const { t } = useTranslation()
  const client = useAdminClient()
  const refresh = useUserRefresh()
  const { open } = useNotification()
  const [isOpen, setIsOpen] = useState(false)
  const [reason, setReason] = useState('')
  const reasonId = useId()
  const ban = useMutation({
    mutationFn: () => client.users.ban({ id: user.id, reason }),
    onSuccess: async () => {
      await refresh(user.id)
      setIsOpen(false)
      open?.({
        type: 'success',
        message: t('usersPage.ban.done', { name: user.name }),
      })
    },
  })
  const canSubmit = reason.trim() !== '' && !ban.isPending

  const onOpenChange = (next: boolean) => {
    if (!next && ban.isPending) {
      return
    }
    if (next) {
      setReason('')
      ban.reset()
    }
    setIsOpen(next)
  }

  const onSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    if (canSubmit) {
      ban.mutate()
    }
  }

  return (
    <Dialog
      open={isOpen}
      onOpenChange={onOpenChange}
    >
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
        >
          {t('usersPage.ban.trigger')}
        </Button>
      </DialogTrigger>
      <DialogContent>
        <form
          noValidate
          onSubmit={onSubmit}
          className="grid gap-4"
        >
          <DialogHeader>
            <DialogTitle>
              {t('usersPage.ban.title', { name: user.name })}
            </DialogTitle>
            <DialogDescription>
              {t('usersPage.ban.description')}
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            <Label htmlFor={reasonId}>{t('usersPage.ban.reason')}</Label>
            <Textarea
              id={reasonId}
              value={reason}
              maxLength={MAX_BAN_REASON_LENGTH}
              readOnly={ban.isPending}
              onChange={(event) => setReason(event.target.value)}
            />
          </div>
          {ban.isError && (
            <p
              role="alert"
              className="text-destructive text-sm"
            >
              {t('usersPage.ban.error', { message: errorMessage(ban.error) })}
            </p>
          )}
          <DialogFooter>
            <DialogClose asChild>
              <Button
                type="button"
                variant="outline"
                disabled={ban.isPending}
              >
                {t('usersPage.cancel')}
              </Button>
            </DialogClose>
            <Button
              type="submit"
              variant="destructive"
              disabled={!canSubmit}
            >
              {t('usersPage.ban.submit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
